import { IA_DOCS_PAUSADA } from "@/lib/pausas";
import React, { useRef, useState } from "react";
import { useCamara } from "@/components/Camara";
import { prepararArchivo } from "@/lib/imagen";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Sheet } from "@/pages/Familia";
import { GastoForm } from "./GastoForm";
import { useConfirmarNoClinico } from "@/components/documentos/ConfirmarNoClinico";
import { OpcionesIA } from "@/components/documentos/PreguntaSubida";

const Opcion = ({ testid, icon: I, label, desc, onClick, grande }) => (
  <button data-testid={testid} onClick={onClick}
    className={grande ? "w-full rounded-2xl bg-[#3A6B56] text-white p-5 flex items-center gap-4 text-left shadow-md min-h-[96px]"
      : "w-full rounded-2xl bg-white border border-[#E5DFD5] p-4 flex items-center gap-4 text-left min-h-[72px] hover:border-[#3A6B56]"}>
    <span className={`shrink-0 rounded-2xl flex items-center justify-center ${grande ? "w-16 h-16 bg-white/15" : "w-12 h-12 bg-[#E8F0EC]"}`}>
      <I size={grande ? 34 : 24} className={grande ? "text-white" : "text-[#3A6B56]"} />
    </span>
    <span>
      <span className={`block font-heading font-bold ${grande ? "text-xl" : "text-lg text-[#1C2421]"}`}>{label}</span>
      <span className={`block text-sm ${grande ? "text-white/85" : "text-[#6E7A74]"}`}>{desc}</span>
    </span>
  </button>
);

// Registrar un gasto: foto (protagonista), subir ticket/factura o a mano.
export const RegistrarGasto = ({ onClose, personaInicial, onGuardado }) => {
  const { subirDocumento, leerGasto, eliminarDocumento, detectarDuplicados } = useData();
  const sinAnalisis = useRef(false);
  const fileRef = useRef(null);
  const onFileRef = useRef(null);
  const camara = useCamara((e) => onFileRef.current(e));
  const [paso, setPaso] = useState("elegir");
  const [doc, setDoc] = useState(null);
  const [datos, setDatos] = useState(null);
  const [dup, setDup] = useState(null);
  const [pendiente, setPendiente] = useState(null);
  const noClinico = useConfirmarNoClinico();

  const onFile = async (e) => {
    const elegido = e.target.files?.[0];
    e.target.value = "";
    if (!elegido) return;
    const file = await prepararArchivo(elegido);
    if (!/\.(pdf|jpe?g|png)$/i.test(file.name)) return toast.error("Formato no admitido. Usa PDF, JPG o PNG.");
    if (file.size > 10 * 1024 * 1024) return toast.error("El archivo supera el límite de 10 MB.");
    if (IA_DOCS_PAUSADA || sinAnalisis.current) return procesar(file);
    setPendiente(file);
    setPaso("modo");
  };

  const procesar = async (file) => {
    setPaso("leyendo");
    let d;
    const sinIA = IA_DOCS_PAUSADA || sinAnalisis.current;
    const extra = sinIA ? { sin_ia: "si", confirma_no_clinico: "si", nombre: `Ticket ${file.name.replace(/\.[^.]+$/, "")}` } : { origen: "gastos" };
    try { d = await subirDocumento(file, extra); setDoc(d); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo subir el ticket."); return setPaso("elegir"); }
    try { const r = await detectarDuplicados(d.id); setDup(r.tiene_fuerte ? r.duplicados.find((c) => c.strength === "fuerte") : null); } catch { setDup(null); }
    if (sinIA) { setDatos(null); return setPaso("form"); }
    try { setDatos(await leerGasto(d.id)); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo leer el ticket. Introduce los datos a mano."); setDatos(null); }
    setPaso("form");
  };

  onFileRef.current = onFile;

  const descartar = async () => {
    if (doc) { try { await eliminarDocumento(doc.id); toast("Ticket descartado. Lo tienes en la papelera por si lo necesitas."); } catch { /* ya eliminado */ } }
    onClose();
  };

  if (paso === "form") return <GastoForm docId={doc?.id} detectado={datos} duplicado={dup} personaInicial={personaInicial} origen="gastos" onClose={descartar} onSaved={(g) => { onClose(); onGuardado?.(g); }} />;
  return (
    <Sheet onClose={onClose} testid="registrar-gasto">
      {camara.inputs}
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*" className="hidden" onChange={onFile} data-testid="gasto-archivo-input" />
      {paso === "modo" && pendiente ? (
        <OpcionesIA prefijo="gasto" archivo={pendiente.name}
          onAnalizar={() => { sinAnalisis.current = false; procesar(pendiente); }}
          onSinIA={() => noClinico.pedir(() => { sinAnalisis.current = true; procesar(pendiente); }, "este ticket")}
          onCancelar={() => { setPendiente(null); setPaso("elegir"); }} />
      ) : paso === "leyendo" ? (
        <div className="py-12 text-center" data-testid="gasto-leyendo">
          <div className="w-16 h-16 mx-auto rounded-full border-4 border-[#E5DFD5] border-t-[#3A6B56] animate-spin" />
          <p className="font-heading text-xl font-bold text-[#1C2421] mt-6">Leyendo el ticket…</p>
          <p className="text-base text-[#6E7A74] mt-1">Después podrás revisar y corregir los datos.</p>
        </div>
      ) : (
        <>
          <h2 className="font-heading text-2xl font-bold text-[#1C2421] pr-12">Registrar un gasto</h2>
          <p className="text-base text-[#6E7A74] mt-1 mb-5">Guarda el ticket y AL DÍA leerá los datos por ti.</p>
          <div className="space-y-3">
            <Opcion grande testid="gasto-opcion-foto" icon={Icons.Camera} label="Hacer una foto" desc="Fotografía el ticket o la factura" onClick={() => { sinAnalisis.current = false; camara.abrir(); }} />
            {camara.esMovil && <Opcion testid="gasto-opcion-galeria" icon={Icons.Image} label="Elegir de la galería" desc="Una foto del ticket que ya tengas en el móvil" onClick={() => { sinAnalisis.current = false; camara.elegirGaleria(); }} />}
            <Opcion testid="gasto-opcion-subir" icon={Icons.Upload} label="Subir ticket o factura" desc="Elige un PDF o una imagen del móvil" onClick={() => { sinAnalisis.current = false; fileRef.current?.click(); }} />
            <Opcion testid="gasto-opcion-manual" icon={Icons.PencilLine} label="Introducir manualmente" desc="Sin ticket: escribe tú los datos" onClick={() => setPaso("form")} />
          </div>
          <p className="text-sm text-[#6E7A74] mt-4" data-testid="gasto-transparencia-ia">AL DÍA analizará el ticket para leer los datos automáticamente.</p>
          <button data-testid="gasto-guardar-sin-analisis" onClick={() => noClinico.pedir(() => { sinAnalisis.current = true; fileRef.current?.click(); })}
            className="mt-1 min-h-[44px] text-sm font-semibold text-[#4A5550] underline underline-offset-4">Guardar sin análisis</button>
          {camara.aviso}
        </>
      )}
      {noClinico.dialogo}
    </Sheet>
  );
};
